import { useRef } from 'react';
import { Box, Button, Tooltip, Typography, useMediaQuery } from '@mui/material';
import SearchBar from '../Search/SearchBar';
import { GridCsvExportOptions, useGridApiContext } from '@mui/x-data-grid-pro';
import { getExportFileName } from '@/utils';
import { DownloadButtonIcon } from './Icons';
import { useAppSelector } from '@/app/hooks';
import { useTranslation } from 'react-i18next';

import MissingItemsLabel from './MissingItemsLabel';

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    title: string;
    subTitle: string;
    search: string;
    showExport: boolean;
    items: any;
    isFetching: boolean;
  }
}

export type CustomToolbarProps = {
  title: string;
  subTitle: string;
  search: string;
  showExport: boolean;
  items: any;
  isFetching: boolean;
};

const CustomToolbar = ({
  title,
  subTitle,
  search,
  showExport,
  items = [],
  isFetching,
}: CustomToolbarProps) => {
  const toolbarRef = useRef<HTMLHeadingElement>(null);
  const siteId = useAppSelector((state) => state.user.siteId);
  const { t } = useTranslation();
  const isDesktop = useMediaQuery('(min-width:900px)');
  const apiRef = useGridApiContext();
  const handleExport = (options: GridCsvExportOptions) => {
    apiRef.current.exportDataAsCsv(options);
  };

  return (
    <>
      {isDesktop && (
        <Box
          sx={{
            padding: '16px',
            display: 'flex',
            flexDirection: { xs: 'row-reverse', md: 'column' },
            flexWrap: { xs: 'wrap', md: 'nowrap' },
          }}
          data-testid="custom-toolbar-container"
          ref={toolbarRef}
        >
          <Box
            sx={{
              height: subTitle && isDesktop ? '64px' : '40px',
              marginBottom: {
                xs: 0,
                md: '12px',
              },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexDirection: 'row',
              flex: { xs: 0, md: 1 },
              marginLeft: { xs: '10px', md: '0' },
            }}
          >
            {isDesktop && (
              <Box>
                <Typography
                  textAlign="left"
                  fontSize="16px"
                  fontWeight="600"
                  data-testid="table-title"
                >
                  {title}
                </Typography>
                {subTitle && (
                  <Typography textAlign="left" fontSize="14px" mt={'12px'}>
                    {subTitle}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          {isDesktop && (
            <Box
              sx={{
                marginBottom: { md: '12px' },
                display: 'flex',
                flex: !isDesktop ? 1 : '0 1 auto',
                height: '40px',
                width: '100%',
                flexDirection: isDesktop ? 'row' : 'column',
              }}
            >
              <SearchBar search={search} />

              {showExport && (
                <Box
                  sx={{
                    padding: 0,
                    ...(!isDesktop
                      ? {
                          display: 'flex',
                          flexWrap: 'nowrap!important',
                          width: '100%',
                        }
                      : {}),
                  }}
                >
                  <Tooltip title={t('assetTable.export')}>
                    <Button
                      onClick={() =>
                        handleExport({
                          fileName: getExportFileName(siteId, title),
                          delimiter: ',',
                          utf8WithBom: true,
                        })
                      }
                      sx={(theme) => ({
                        borderRadius: '8px',
                        border: `1px solid ${theme.palette.text.disabled}`,
                        width: '39px!important',
                        height: '39px!important',
                        minWidth: '36px',
                        padding: 0,
                        color: theme.palette.text.primary,
                        marginLeft: '5px',
                      })}
                      data-testid="table-export-button"
                    >
                      <DownloadButtonIcon
                        viewBox="-1 -1 20 20"
                        style={{
                          width: '20px',
                          height: '20px',
                        }}
                      />
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
          {!isFetching && (
            <MissingItemsLabel
              search={search}
              items={items}
            ></MissingItemsLabel>
          )}
        </Box>
      )}
    </>
  );
};

export default CustomToolbar;
